package org.example.zone360.utils

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.SideEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.css.CSSLengthNumericValue
import com.varabyte.kobweb.compose.css.CSSLengthOrPercentageNumericValue
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.TextAlign
import com.varabyte.kobweb.compose.css.WhiteSpace
import com.varabyte.kobweb.compose.css.WordBreak
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.cursor
import com.varabyte.kobweb.compose.ui.modifiers.flexWrap
import com.varabyte.kobweb.compose.ui.modifiers.fontFamily
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.id
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.modifiers.onClick
import com.varabyte.kobweb.compose.ui.modifiers.onMouseEnter
import com.varabyte.kobweb.compose.ui.modifiers.onMouseLeave
import com.varabyte.kobweb.compose.ui.modifiers.textAlign
import com.varabyte.kobweb.compose.ui.modifiers.whiteSpace
import com.varabyte.kobweb.compose.ui.modifiers.width
import com.varabyte.kobweb.compose.ui.modifiers.wordBreak
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import kotlinx.browser.document
import org.example.zone360.theme.toSitePalette
import org.jetbrains.compose.web.css.FlexWrap
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Div
import org.w3c.dom.HTMLElement
import kotlin.random.Random

// ─────────────────────────────────────────────────────────────────────────────
// JS interop — marked.js must be loaded in build.gradle.kts:
//   script { src = "https://cdn.jsdelivr.net/npm/marked/marked.min.js" }
// ─────────────────────────────────────────────────────────────────────────────

private fun markdownToHtml(markdown: String): String {
    return try {
        // marked.js v4+ API — returns full HTML from raw markdown
        js("marked.parse(markdown)") as String
    } catch (e: Throwable) {
        // Safety fallback: escape HTML and wrap in <p>
        "<p style=\"white-space:pre-wrap;margin:0;\">" +
                markdown
                    .replace("&", "&amp;")
                    .replace("<", "&lt;")
                    .replace(">", "&gt;") +
                "</p>"
    }
}

// ─────────────────────────────────────────────────────────────────────────────
// MarkdownRenderer
//
// Identical parameter signature to FieldTitleTextNormalSimpleForTest.
// Just swap the function name — every call site works unchanged.
// ─────────────────────────────────────────────────────────────────────────────

@Composable
fun MarkdownRenderer(
    text: String,
    isStreaming: Boolean = false,
    width: CSSLengthOrPercentageNumericValue = 0.px,
    color: Color? = null,
    fontSize: CSSLengthNumericValue? = null,
    textAlign: TextAlign = TextAlign.Start,
    fontFamily: String = Constants.FONT_FAMILY,
    fontWeight: Int = 400,
    onClick: () -> Unit = {},
    isClickable: Boolean? = false,
    marginTop: CSSLengthNumericValue = 0.px,
    marginLeft: CSSLengthNumericValue = 0.px,
    marginRight: CSSLengthNumericValue = 0.px,
    marginBottom: CSSLengthNumericValue = 0.px,
    flexWrap: FlexWrap = FlexWrap.Nowrap,
    whiteSpace: WhiteSpace = WhiteSpace.Normal,
    onMouseEnter: (() -> Unit)? = null,
    onMouseLeave: (() -> Unit)? = null,
    id: String = "",
    isNotAllowed: Boolean = false,
    wordBreak: WordBreak = WordBreak.Normal
) {
    val sitePalette = ColorMode.current.toSitePalette()
    val widthModifier = if (width == 0.px) Modifier else Modifier.width(width)

    // Unique DOM id so LaunchedEffect can find this specific element
    val containerId = remember(id) {
        if (id.isNotBlank()) "md-$id" else "md-${Random.nextInt(100_000, 999_999)}"
    }

    // Convert markdown → HTML once per unique text value
    // NOTE: we do NOT call cleanMarkdownAndDetect here — that strips the
    // markdown syntax that marked.js needs to render properly.
    val htmlContent = remember(text) { markdownToHtml(text) }
    var prevTextLength by remember { mutableStateOf(0) }

    Div(
        attrs = widthModifier
            .margin(
                top = marginTop,
                bottom = marginBottom,
                left = marginLeft,
                right = marginRight
            )
            .color(color ?: sitePalette.textFieldTextColor.rgb)
            .fontFamily(fontFamily)
            .fontSize(fontSize ?: 13.px)
            .fontWeight(fontWeight)
            .textAlign(textAlign)
            .flexWrap(flexWrap)
            .whiteSpace(whiteSpace)
            .wordBreak(wordBreak)
            .id(containerId)
            .onMouseEnter { onMouseEnter?.invoke() }
            .onMouseLeave { onMouseLeave?.invoke() }
            .cursor(
                when {
                    isClickable == true -> Cursor.Pointer
                    isNotAllowed        -> Cursor.NotAllowed
                    else                -> Cursor.Default
                }
            )
            .onClick { onClick() }
            .toAttrs()
    )


// ── Streaming: append only NEW chunk per token ──────────────────────
// ── Streaming: SideEffect — har token pe guaranteed fire ────────────
    if (isStreaming) {
        SideEffect {
            val el = document.getElementById(containerId) as? HTMLElement ?: return@SideEffect

            val alreadyRendered = el.getAttribute("data-rendered-len")?.toIntOrNull() ?: 0
            val newChunk = if (text.length > alreadyRendered) text.substring(alreadyRendered) else ""
            el.setAttribute("data-rendered-len", text.length.toString())

            if (newChunk.isNotEmpty()) {
                val span = document.createElement("span")
                span.className = "token-slide-in"
                span.textContent = newChunk
                el.appendChild(span)
            }
        }
    }

// ── Done: render markdown with line animation ────────────────────────
    LaunchedEffect(isStreaming) {
        if (isStreaming) return@LaunchedEffect
        if (text.isBlank()) return@LaunchedEffect

        val el = document.getElementById(containerId) as? HTMLElement ?: return@LaunchedEffect
        el.removeAttribute("data-rendered-len")

        val fullHtml = markdownToHtml(text)
        val blocks = fullHtml.trim().split("\n").filter { it.trim().isNotEmpty() }

        var textLineIndex = 0
        val animated = buildString {
            blocks.forEach { line ->
                val trimmed = line.trim()
                val isTableLine = trimmed.startsWith("<table") || trimmed.startsWith("<thead") ||
                        trimmed.startsWith("<tbody") || trimmed.startsWith("<tr") ||
                        trimmed.startsWith("<th") || trimmed.startsWith("<td") ||
                        trimmed.startsWith("</")
                if (isTableLine) {
                    append(line).append("\n")
                } else {
                    val delayMs = textLineIndex * 80
                    append("""<span class="chat-line-enter" style="animation-delay:${delayMs}ms">$line</span>""")
                    append("\n")
                    textLineIndex++
                }
            }
        }
        el.innerHTML = animated
    }
}

// ─────────────────────────────────────────────────────────────────────────────
// injectMarkdownStyles()
//
// Call ONCE at app startup in App.kt:
//
//   LaunchedEffect(Unit) { injectMarkdownStyles() }
//
// Uses [id^="md-"] selector so it scopes ONLY to MarkdownRenderer divs and
// never leaks into the rest of the app.
// ─────────────────────────────────────────────────────────────────────────────

fun injectMarkdownStyles(primaryColor: String = "#FF6B35") {
    if (document.getElementById("zone360-md-styles") != null) return

    val css = """
        /* ── Zone360 Markdown Renderer styles ── */

        [id^="md-"] {
            box-sizing: border-box;
        }

        /* Paragraphs */
        [id^="md-"] p {
            margin: 0 0 6px 0;
            line-height: 1.6;
        }
        [id^="md-"] p:last-child {
            margin-bottom: 0;
        }
        /* marked.js wraps li content in <p> — flatten to remove double-gap */
        [id^="md-"] li > p {
            margin: 0;
            display: inline;
        }
        [id^="md-"] li > p + p {
            display: block;
            margin-top: 2px;
        }

        /* Headings */
        [id^="md-"] h1, [id^="md-"] h2, [id^="md-"] h3,
        [id^="md-"] h4, [id^="md-"] h5, [id^="md-"] h6 {
            font-weight: 700;
            line-height: 1.3;
            margin: 10px 0 4px 0;
            color: #111111;
        }
        [id^="md-"] h1 { font-size: 1.4em; }
        [id^="md-"] h2 { font-size: 1.25em; }
        [id^="md-"] h3 { font-size: 1.1em; }
        [id^="md-"] h4,
        [id^="md-"] h5,
        [id^="md-"] h6 { font-size: 1em; }

        /* Lists - tight like Claude */
        [id^="md-"] ul,
        [id^="md-"] ol {
            margin: 4px 0 6px 0;
            padding-left: 20px;
        }
        [id^="md-"] li {
            margin-bottom: 3px;
            line-height: 1.6;
        }
        [id^="md-"] li:last-child {
            margin-bottom: 0;
        }
        [id^="md-"] li > ul,
        [id^="md-"] li > ol {
            margin: 2px 0 0 0;
        }

        /* Inline code */
        [id^="md-"] code {
            background: #F3F4F6;
            border: 1px solid #E5E7EB;
            border-radius: 4px;
            padding: 1px 5px;
            font-size: 0.88em;
            font-family: 'IBM Plex Mono', monospace;
            color: #d63384;
        }

        /* Code block */
        [id^="md-"] pre {
            background: #1E1E2E;
            border-radius: 8px;
            padding: 14px 16px;
            overflow-x: auto;
            margin: 10px 0;
        }
        [id^="md-"] pre code {
            background: transparent;
            border: none;
            color: #CDD6F4;
            font-size: 13px;
            padding: 0;
        }

        /* Blockquote */
        [id^="md-"] blockquote {
            border-left: 3px solid $primaryColor;
            margin: 10px 0;
            padding: 6px 14px;
            background: #FFF8F5;
            border-radius: 0 6px 6px 0;
            color: #555555;
            font-style: italic;
        }
        [id^="md-"] blockquote p {
            margin: 0;
        }

        /* Tables */
        [id^="md-"] table {
            width: 100%;
            border-collapse: collapse;
            margin: 6px 0;
            font-size: 13px;
            border-radius: 8px;
            overflow: hidden;
            box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
            display: table;
        }
        [id^="md-"] thead tr {
            background-color: $primaryColor;
            color: #ffffff;
        }
        [id^="md-"] th {
            padding: 10px 14px;
            text-align: left;
            font-weight: 600;
            letter-spacing: 0.03em;
            white-space: nowrap;
        }
        [id^="md-"] td {
            padding: 9px 14px;
            border: 1px solid #EDEDED;
            vertical-align: top;
            line-height: 1.5;
            color: #333333;
        }
        [id^="md-"] tbody tr:nth-child(odd)  { background-color: #FFFFFF; }
        [id^="md-"] tbody tr:nth-child(even) { background-color: #FFF8F5; }
        [id^="md-"] tbody tr:hover           { background-color: #FFEDE5; transition: background-color 150ms; }
        [id^="md-"] td:first-child           { font-weight: 600; color: #1A1A1A; }

        /* Horizontal rule */
        [id^="md-"] hr {
            border: none;
            border-top: 1px solid #E5E7EB;
            margin: 14px 0;
        }

        /* Links */
        [id^="md-"] a {
            color: $primaryColor;
            text-decoration: underline;
            text-underline-offset: 2px;
        }
        [id^="md-"] a:hover { opacity: 0.8; }

        /* Inline formatting */
        [id^="md-"] strong { font-weight: 700; color: #111111; }
        [id^="md-"] em     { font-style: italic; }
        [id^="md-"] del    { text-decoration: line-through; color: #888888; }
    """.trimIndent()

    val style = document.createElement("style")
    style.id = "zone360-md-styles"
    style.textContent = css
    document.head?.appendChild(style)
}

fun injectChatAnimationStyles() {
    if (document.getElementById("zone360-chat-anim") != null) return

    val css = """
        @keyframes tokenSlideIn {
            from { opacity: 0; transform: translateX(-6px); }
            to   { opacity: 1; transform: translateX(0);    }
        }
        .token-slide-in {
            display: inline;
            opacity: 0;
            animation: tokenSlideIn 0.4s ease-out forwards;
        }
        @keyframes lineSlideIn {
            from { opacity: 0; transform: translateX(-14px); }
            to   { opacity: 1; transform: translateX(0);     }
        }
        .chat-line-enter {
            display: block;
            opacity: 0;
            animation: lineSlideIn 0.3s ease-out forwards;
        }
    """.trimIndent()

    val style = document.createElement("style")
    style.id = "zone360-chat-anim"
    style.textContent = css
    document.head?.appendChild(style)
}